import React from "react";
import {observer} from "mobx-react";
import {useBetween} from "use-between";
import {useInjection} from "inversify-react";
import {useNavigate} from "react-router-dom";
import {AppController} from "@dropDesk/presentation/app/app.controller";
import ModalMain from "@dropDesk/presentation/components/modals/modal_main";
import DropDeskIcon from "@dropDesk/storage/icons";
import {ICONS_DROPDESK} from "@dropDesk/storage/icons/icons.d";
import {
    markAppModalAsSeen,
    markInternalChatModalAsSeen,
    markReleaseAsSeen,
    useModalApp,
    useModalInternalChat,
    useModalReleases,
} from "@dropDesk/presentation/pages/dashboard/ui/modals/handle_change_visible";
import ModalAppContent from "@dropDesk/presentation/pages/dashboard/ui/modals/content";
import ModalInternalChatContent from "@dropDesk/presentation/pages/dashboard/ui/modals/content_internal_chat";
import {RoutesEnum} from "@dropDesk/domain/entities/routes/routes_enum";
import {
    Bullet,
    ButtonConfirm,
    CloseButton,
    HeaderContent,
    IconWrapper,
    LinkText,
    MoreText,
    ReleaseDescription,
    ReleaseList,
    ReleaseRow,
    ReleaseSeparator,
    ReleaseTextContainer,
    ReleaseTitle,
    ReleasesContainer,
    ReleasesFooter,
    Subtitle,
    Title,
    TitleAccent,
} from "@dropDesk/presentation/pages/dashboard/ui/modals/styled";

const releases = [
    {
        title: "Último atendimento do contato",
        description:
            "Na listagem de contatos do menu Chat, visualize o último atendimento vinculado ao contato, facilitando o acompanhamento do histórico sem sair da tela.",
    },
    {
        title: "Filtro por empresa nos contatos",
        description:
            "Filtre a listagem de contatos do menu Chat por empresa, tornando mais rápida a localização de clientes e contatos associados.",
    },
    {
        title: "Período na listagem de atendimentos",
        description:
            "A listagem do menu Atendimentos passa a considerar um período, reduzindo o volume de dados carregados e tornando a consulta mais leve e responsiva.",
    },
    {
        title: "Pesquisa aprimorada em atendimentos",
        description:
            "A busca no menu Atendimentos volta a contemplar telefone e nome do contato, além do protocolo do atendimento.",
    },
    {
        title: "Carregamento mais rápido das mensagens",
        description:
            "A listagem de mensagens foi otimizada para carregar com maior agilidade.",
    },
    {
        title: "Carregamento mais rápido dos atendimentos",
        description:
            "A listagem de atendimentos foi otimizada para exibir os resultados com maior rapidez.",
    },
];

const ModalReleases = observer(({onClose}: {onClose?: () => void}) => {
    const appController = useInjection(AppController);
    const colors = appController.theme.colorScheme;
    const {visibleModalReleases, setVisibleModalReleases} = useBetween(useModalReleases);

    const handleClose = () => {
        if (appController.user?.id) {
            markReleaseAsSeen(appController.user.id);
        }
        setVisibleModalReleases(false);
        if (onClose) {
            onClose();
        }
    };

    const iconBackgroundColor = `${colors.accent}22`;

    return (
        <ModalMain
            open={visibleModalReleases}
            onRequestClose={handleClose}
            width={580}
            ignoreCloseOnClickOutside={true}
            renderComponent={
                <ReleasesContainer background={colors.onBackground}>
                    <HeaderContent background={colors.onBackground}>
                        <CloseButton
                            color={colors.hint}
                            hoverBackground={colors.buttonOnBackground}
                            onClick={handleClose}
                        >
                            <DropDeskIcon
                                icon={ICONS_DROPDESK.close}
                                size={16}
                                color={colors.hint}
                            />
                        </CloseButton>
                        <IconWrapper background={iconBackgroundColor}>
                            <DropDeskIcon
                                icon={ICONS_DROPDESK.megaphone}
                                size={42}
                                color={colors.accent}
                            />
                        </IconWrapper>
                        <Title color={colors.text}>
                            Confira as novidades no{" "}
                            <TitleAccent color={colors.accent}>DropDesk</TitleAccent>
                            .
                        </Title>
                        <Subtitle color={colors.hint}>
                            Melhorias e novos recursos para tornar sua experiência ainda melhor.
                        </Subtitle>
                    </HeaderContent>

                    <ReleaseList background={colors.onBackground}>
                        {releases.map((item, index) => (
                            <ReleaseRow key={index}>
                                <Bullet color={colors.text}/>
                                <ReleaseTextContainer>
                                    <ReleaseTitle color={colors.text}>{item.title}</ReleaseTitle>
                                    <ReleaseSeparator color={colors.text}>—</ReleaseSeparator>
                                    <ReleaseDescription color={colors.text}>
                                        {item.description}
                                    </ReleaseDescription>
                                </ReleaseTextContainer>
                            </ReleaseRow>
                        ))}
                        <MoreText color={colors.text}>E muito mais!</MoreText>
                    </ReleaseList>

                    <ReleasesFooter background={colors.onBackground} border={colors.border}>
                        <LinkText
                            color={colors.accent}
                            href={process.env.URL_RELEASES}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Saiba mais no nosso site ↗
                        </LinkText>
                        <ButtonConfirm background={colors.accent} onClick={handleClose}>
                            Entendi, obrigado!
                        </ButtonConfirm>
                    </ReleasesFooter>
                </ReleasesContainer>
            }
        />
    );
});

export const ModalApp = observer(({onClose}: {onClose?: () => void}) => {
    const appController = useInjection(AppController);
    const navigate = useNavigate();
    const colors = appController.theme.colorScheme;
    const {visibleModalApp, setVisibleModalApp} = useBetween(useModalApp);
    const isAdmin = appController.user?.permissionAdmin ?? false;

    const markAndHide = () => {
        if (appController.user?.id) {
            markAppModalAsSeen(appController.user.id);
        }
        setVisibleModalApp(false);
    };

    const handleClose = () => {
        markAndHide();
        if (onClose) {
            onClose();
        }
    };

    const handleEnjoy = () => {
        markAndHide();
        navigate(RoutesEnum.extensions);
    };

    return (
        <ModalMain
            open={visibleModalApp}
            onRequestClose={handleClose}
            width={960}
            ignoreCloseOnClickOutside={true}
            renderComponent={
                <ModalAppContent
                    colors={colors}
                    isAdmin={isAdmin}
                    onClose={handleClose}
                    onEnjoy={handleEnjoy}
                />
            }
        />
    );
});

export const ModalInternalChat = observer(() => {
    const appController = useInjection(AppController);
    const navigate = useNavigate();
    const colors = appController.theme.colorScheme;
    const {visibleModalInternalChat, setVisibleModalInternalChat} = useBetween(useModalInternalChat);
    const isAdmin = appController.user?.permissionAdmin ?? false;

    const handleClose = () => {
        if (appController.user?.id) {
            markInternalChatModalAsSeen(appController.user.id);
        }
        setVisibleModalInternalChat(false);
    };

    const handleEnjoy = () => {
        handleClose();
        navigate(RoutesEnum.extensions);
    };

    return (
        <ModalMain
            open={visibleModalInternalChat}
            onRequestClose={handleClose}
            width={960}
            ignoreCloseOnClickOutside={true}
            renderComponent={
                <ModalInternalChatContent
                    colors={colors}
                    isAdmin={isAdmin}
                    onClose={handleClose}
                    onEnjoy={handleEnjoy}
                />
            }
        />
    );
});

export default ModalReleases;
